import type { AdminMetricsWindow } from '@carcheck/shared';
import { ADMIN_METRICS_WINDOWS } from '@carcheck/shared';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { tokens } from '../../../design';

/**
 * Top-bar analytics range selector (24ч / 7д / 30д / 90д) — mirrors the
 * `.topbar__range` segmented control in `design/mockups/admin/dashboard.html`.
 * Drives the dashboard `window` state → `useAdminMetrics(window)` refetch.
 *
 * Values come from the shared `ADMIN_METRICS_WINDOWS` allow-list (the same
 * list the backend Zod-validates), so the control can never emit a window
 * the server rejects.
 */
export interface RangeSelectorProps {
  value: AdminMetricsWindow;
  onChange: (window: AdminMetricsWindow) => void;
}

export const RangeSelector = ({ value, onChange }: RangeSelectorProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={value}
      onChange={(_, next: AdminMetricsWindow | null) => {
        if (next) onChange(next);
      }}
      aria-label={t('dashboard.range.label')}
      sx={{
        background: tokens.surface.elevated,
        borderRadius: 999,
        p: '3px',
        '& .MuiToggleButton-root': {
          border: 0,
          borderRadius: '999px !important',
          px: 1.25,
          py: 0.5,
          fontSize: 11,
          fontWeight: 600,
          color: tokens.text.tertiary,
          textTransform: 'none',
          '&.Mui-selected': {
            background: tokens.surface.base,
            color: tokens.text.primary,
            boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
            '&:hover': { background: tokens.surface.base },
          },
        },
      }}
    >
      {ADMIN_METRICS_WINDOWS.map((w) => (
        <ToggleButton key={w} value={w} aria-label={t(`dashboard.range.${w}`)}>
          {t(`dashboard.range.${w}`)}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};
